/**
 * THE RESUME ADVISORY SURFACE (staleness gate, commit 5).
 *
 * INFORMS; NEVER BLOCKS. Rendered from the server's advisory on the two
 * surfaces that read GET /procurement/{id} - the wizard (via
 * WizardMasterReferenceSurfaces) and the detail page - and from the advisory a
 * boundary refusal carries. The buyer keeps reading, and the next boundary
 * they continue to is what refuses. Styled with the amber panel the no-fit
 * state already uses.
 *
 * GROUPED BY THE STEP WHERE EACH THING IS FIXED (Ashok ruling, 2026-09-17).
 * One block per step, in wizard order; under it, what is fixed there - stale
 * master references and past-date hard stops. A buyer goes to a step and
 * fixes everything there, instead of discovering one problem per boundary
 * (PROC-000369).
 *
 * THE STEP NAME IS A LINK ONLY WHERE IT CAN BE ONE. `stepLink` is supplied by
 * the wizard: `isNavigable(step)` and `navigate(step)`, which calls
 * setCurrentStep and nothing else - no cap, no unlock, no request. Without it
 * (the detail page, which has no wizard), or where the wizard says the step
 * is not navigable (the step the buyer is on, a step not yet reached, a
 * completed procurement), the name renders as plain text - never a dead link.
 *
 * WITHIN A STEP, ONE ENTRY PER CODE, NOT PER REFERENCE (Ashok ruling,
 * 2026-09-15). PROC-000412 rendered 51 identical lines for one retired code.
 * The affected line numbers render INLINE beneath the entry - always visible,
 * no toggle (Ashok ruling, 2026-09-15) - plain up to PLAIN_LINE_LIST_MAX
 * numbers and compressed into ranges above it.
 * `totalLines` is the document's final line count when the surface knows it.
 */
import { AlertTriangle } from 'lucide-react';
import {
  ADVISORY_STEP_NAMES,
  advisoryHasItems,
  describeDateGroup,
  describeStepAdvisoryLead,
  describeStepAdvisoryTitle,
  describeStepMasterGroup,
  formatAffectedLines,
  formatBannerLineNumbers,
  groupAdvisoryByStep,
} from '@/lib/masterReferenceStaleness';

const LineList = ({ lineNumbers }) => (
  lineNumbers.length > 0 ? (
    <p className="mt-1 text-amber-800" data-testid="master-reference-advisory-lines">
      {formatAffectedLines(lineNumbers.length, formatBannerLineNumbers(lineNumbers))}
    </p>
  ) : null
);

const StepName = ({ step, stepLink }) => {
  const name = ADVISORY_STEP_NAMES[step] || step;
  if (stepLink && stepLink.isNavigable(step)) {
    return (
      <button
        type="button"
        className="underline underline-offset-2 text-left font-semibold text-amber-900 hover:text-amber-950"
        onClick={() => stepLink.navigate(step)}
        data-testid={`advisory-step-link-${step}`}
      >
        {name}
      </button>
    );
  }
  const here = stepLink && stepLink.currentStep === step;
  return (
    <span className="font-semibold text-amber-900" data-testid={`advisory-step-name-${step}`}>
      {name}{here ? ' (this step)' : ''}
    </span>
  );
};

const MasterReferenceAdvisoryBanner = ({ advisory, totalLines = null, className = '', stepLink = null }) => {
  if (!advisoryHasItems(advisory)) return null;
  const blocks = groupAdvisoryByStep(advisory);
  if (!blocks.length) return null;
  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-lg p-4 ${className}`}
      data-testid="master-reference-advisory"
      role="status"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">{describeStepAdvisoryTitle(blocks)}</p>
          <p className="text-xs text-amber-800 mt-1">{describeStepAdvisoryLead(blocks)}</p>
          <div className="mt-2 space-y-3">
            {blocks.map((block) => (
              <section key={block.step} className="text-xs" data-testid="advisory-step-block" data-step={block.step}>
                <p><StepName step={block.step} stepLink={stepLink} /></p>
                <ul className="mt-1 space-y-2">
                  {block.masterGroups.map((group) => (
                    <li
                      key={`${group.master}:${group.code || 'none'}`}
                      className="text-amber-900"
                      data-testid="master-reference-advisory-item"
                      data-code={group.code || 'none'}
                      data-master={group.master}
                    >
                      <span data-testid="master-reference-advisory-sentence">{describeStepMasterGroup(group, totalLines)}</span>
                      <LineList lineNumbers={group.lineNumbers} />
                    </li>
                  ))}
                  {block.dateGroups.map((group) => (
                    <li key={`date:${group.code}`} className="text-amber-900" data-testid="advisory-date-item" data-code={group.code}>
                      <span data-testid="advisory-date-sentence">{describeDateGroup(group, totalLines)}</span>
                      <LineList lineNumbers={group.lineNumbers} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterReferenceAdvisoryBanner;
